/**
 * GLMCouplingPage — General Lake Model coupling (simplified 1-D vertical)
 *
 * Hipsey, Bruce & Hamilton 2014. Full GLM runs in Fortran/C externally; this
 * page provides a simplified 1-D vertical analogue and exports a GLM-compatible
 * setup configuration that the user can run in the native GLM CLI.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Layers, ArrowLeft, Info, Download } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';
import { toast } from 'sonner';

interface ModSpec { waterDepthM: number; mountingHeightM: number; waterTempC: number; }
const MOD_DEFAULTS: ModSpec = { waterDepthM: 3.0, mountingHeightM: 0.40, waterTempC: 12 };

// Months for the GLM-style vertical profile preview
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const SURF_T = [5.0,5.4,7.8,10.3,13.5,16.3,18.6,18.4,15.7,12.2,8.4,5.7];
const BED_T  = [4.9,5.0,6.5, 8.5,10.0,11.5,12.0,12.0,11.5,10.5,8.5,6.0];

export default function GLMCouplingPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('glm_coupling');
  const [spec, setSpec] = useState<ModSpec>(() => ({ ...MOD_DEFAULTS, ...loadPersisted<Partial<ModSpec>>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) as ModSpec }));
  const [selectedMonth, setMonth] = useState(6);   // July

  useEffect(() => {
    const unsub = onPersistChange(k => {
      if (k === KEYS.DIFFUSER_MODULE) setSpec({ ...MOD_DEFAULTS, ...loadPersisted<Partial<ModSpec>>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) as ModSpec });
    });
    return () => unsub();
  }, []);

  // Build a vertical profile per month — linear with stratified break
  const profile = useMemo(() => {
    const m = selectedMonth;
    const T_surf = SURF_T[m];
    const T_bed = BED_T[m];
    const D = spec.waterDepthM;
    const layers = 12;
    return Array.from({ length: layers }, (_, i) => {
      const z = (i / (layers - 1)) * D;
      // Idealised summer thermocline at 0.4D
      const fStrat = m >= 4 && m <= 8 ? 1 : 0.1;
      const z_therm = 0.4 * D * fStrat;
      let T = z < z_therm ? T_surf
                          : T_surf - (T_surf - T_bed) * Math.min(1, (z - z_therm) / Math.max(0.1, D - z_therm));
      // Saturation DO
      const DO_sat = 14.652 - 0.41022 * T + 0.0079910 * T * T - 0.000077774 * T * T * T;
      const DO = z < z_therm ? DO_sat * 0.95 : DO_sat * Math.max(0.2, 1 - (z - z_therm) / Math.max(0.1, D - z_therm) * (m >= 5 && m <= 8 ? 0.85 : 0.3));
      return { z, T, DO, DO_sat };
    });
  }, [selectedMonth, spec.waterDepthM]);

  const exportNML = () => {
    const nml = `&glm_setup
   sim_name = 'Barcombe_RB'
   max_layers = 50
   min_layer_vol = 0.5
   min_layer_thick = 0.15
   max_layer_thick = 0.50
/
&morphometry
   lake_name = 'Barcombe Reservoir'
   latitude = 50.91
   longitude = -0.06
   bsn_len = 250
   bsn_wid = 140
   bsn_vals = 4
   H = 0, 1, 2, ${spec.waterDepthM}
   A = 0, 8000, 18000, 34000
/
&time
   timefmt = 2
   start = '2026-01-01 00:00:00'
   stop = '2026-12-31 23:00:00'
   dt = 3600
   num_days = 365
   timezone = 0
/
&meteorology
   met_sw = .true.
   meteo_fl = 'midas_barcombe.csv'
   wind_factor = 1.0
   sw_factor = 1.0
   atm_stab = 0
/
&output
   out_dir = './output'
   out_fn = 'output'
   nsave = 24
/
&inflow
   num_inflows = 1
   names_of_strms = 'River_Ouse'
   inflow_fl = 'inflow_ouse.csv'
/
&outflow
   num_outlet = 1
   outl_elvs = 0.5
   bsn_len_outl = 30
   bsn_wid_outl = 30
   outflow_fl = 'abstraction.csv'
/
! ── Aeration source from active session ──
&surface_drag
   coef_mix_conv = 0.20
   coef_wind_stir = 0.23
/
`;
    const blob = new Blob([nml], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'glm_barcombe.nml';
    a.click();
    toast.success('GLM .nml setup exported');
  };

  // Visual rendering of the depth profile
  const svgW = 720, svgH = 280;
  const padL = 60, padR = 60, padT = 20, padB = 25;
  const plotW = svgW - padL - padR;
  const plotH = svgH - padT - padB;
  const zMax = spec.waterDepthM;

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={Layers}
        title="Module — GLM Lake-Physics Coupling"
        subtitle="Hipsey et al. 2014. Simplified 1-D vertical profile + GLM-compatible .nml export"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${
              enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'
            }`}>
              <input type="checkbox" checked={enabled} onChange={() => toggle('glm_coupling')} className="accent-emerald-500" />
              Module {enabled ? 'ENABLED' : 'DISABLED'}
            </label>
            <button onClick={exportNML}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-500">
              <Download className="w-4 h-4" /> Export .nml
            </button>
            <button onClick={() => navigate('/modules')}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Modules
            </button>
          </div>
        }
      />

      <ModuleConflictBanner moduleId="glm_coupling" />

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Selected month: <span className="text-rb-brand">{MONTHS[selectedMonth]}</span></div>
        <div className="flex flex-wrap gap-1.5">
          {MONTHS.map((m, i) => (
            <button key={m} onClick={() => setMonth(i)}
              className={`px-3 py-1.5 rounded text-xs font-semibold transition ${
                i === selectedMonth ? 'bg-rb-brand text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}>
              {m}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Vertical T(z) and DO(z) profile</div>
        <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full h-auto bg-slate-950 rounded">
          {/* T axis (bottom) */}
          <line x1={padL} y1={padT} x2={padL} y2={padT + plotH} stroke="#64748b" strokeWidth={1} />
          <line x1={padL} y1={padT + plotH} x2={padL + plotW} y2={padT + plotH} stroke="#64748b" strokeWidth={1} />
          <text x={padL + plotW / 2} y={padT + plotH + 18} textAnchor="middle" fontSize={10} fill="#94a3b8">T (°C) / DO (mg/L)</text>
          <text x={20} y={padT + plotH / 2} textAnchor="middle" fontSize={10} fill="#94a3b8" transform={`rotate(-90, 20, ${padT + plotH / 2})`}>Depth z (m)</text>
          {/* gridlines */}
          {[0, 0.25, 0.5, 0.75, 1].map(f => {
            const yp = padT + f * plotH;
            const zv = f * zMax;
            return (
              <g key={f}>
                <line x1={padL} y1={yp} x2={padL + plotW} y2={yp} stroke="#1e293b" strokeWidth={0.4} />
                <text x={padL - 4} y={yp + 3} textAnchor="end" fontSize={9} fill="#94a3b8">{zv.toFixed(1)}</text>
              </g>
            );
          })}
          {[0, 5, 10, 15, 20, 25].map(t => {
            const xp = padL + (t / 25) * plotW;
            return (
              <g key={t}>
                <line x1={xp} y1={padT} x2={xp} y2={padT + plotH} stroke="#1e293b" strokeWidth={0.4} />
                <text x={xp} y={padT + plotH + 12} textAnchor="middle" fontSize={9} fill="#94a3b8">{t}</text>
              </g>
            );
          })}
          {/* T profile */}
          <polyline fill="none" stroke="#ef4444" strokeWidth={2.5}
            points={profile.map(p => `${padL + (p.T / 25) * plotW},${padT + (p.z / zMax) * plotH}`).join(' ')} />
          {/* DO_sat dashed */}
          <polyline fill="none" stroke="#94a3b8" strokeWidth={1} strokeDasharray="3 3"
            points={profile.map(p => `${padL + (p.DO_sat / 25) * plotW},${padT + (p.z / zMax) * plotH}`).join(' ')} />
          {/* DO actual */}
          <polyline fill="none" stroke="#38bdf8" strokeWidth={2.5}
            points={profile.map(p => `${padL + (p.DO / 25) * plotW},${padT + (p.z / zMax) * plotH}`).join(' ')} />
          {/* legend */}
          <g transform={`translate(${padL + 8}, 5)`}>
            <line x1={0} y1={4} x2={20} y2={4} stroke="#ef4444" strokeWidth={2.5} />
            <text x={24} y={7} fontSize={9} fill="#ef4444">Water T</text>
            <line x1={70} y1={4} x2={90} y2={4} stroke="#38bdf8" strokeWidth={2.5} />
            <text x={94} y={7} fontSize={9} fill="#38bdf8">DO</text>
            <line x1={120} y1={4} x2={140} y2={4} stroke="#94a3b8" strokeWidth={1} strokeDasharray="3 3" />
            <text x={144} y={7} fontSize={9} fill="#94a3b8">DO sat</text>
          </g>
        </svg>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> Methodology
        </div>
        <div className="text-xs text-slate-400 leading-relaxed space-y-2">
          <p>
            <strong className="text-emerald-300">In-browser:</strong> a simplified 1-D vertical analogue
            computes monthly T(z) and DO(z) using the seasonal climate forcing and the active session
            water depth. The thermocline depth defaults to 0.4 × D during stratified months (May–Sep) and
            collapses to fully mixed otherwise.
          </p>
          <p>
            <strong className="text-sky-300">External GLM run:</strong> the "Export .nml" button generates
            a GLM-compatible Namelist setup for Barcombe with the active session geometry. Drop the file
            into a GLM working directory along with the standard <code>met.csv</code>, <code>inflow.csv</code>
            and <code>outflow.csv</code> files, then run <code>glm_3.x</code> from the CLI. GLM output
            files (NetCDF) can be imported back here in a future revision.
          </p>
          <p className="italic text-slate-500">
            References: Hipsey, Bruce & Hamilton 2014; Stepanenko et al. 2016 (LAKE 2.0).
          </p>
        </div>
      </div>
    </div>
  );
}
